import { useAuth } from "@/lib/auth/AuthContext";
import { isSuperadminRole, USER_ROLE_LABELS } from "@/lib/auth/roles";
import { useUsers } from "@/hooks/useUsers";

// ---------------------------------------------------------------------------
// ImpersonateBar — der EINE Ort, an dem eine Vertretung beginnt und endet
// ---------------------------------------------------------------------------
// STABIL-13 (30.07.2026): Daniel meldete, dass er nach «Impersonation
// beenden» von seiner Startseite aus niemanden mehr vertreten konnte. Die
// Liste der vertretbaren Personen holte sich diese Leiste bis dahin selbst —
// mit `useState` + `useEffect` + `apiGet`, als einzige Stelle im Haus. Das
// hatte zwei Folgen:
//
//   1. Ein Fehlschlag war endgueltig. Der `catch` setzte die Liste still auf
//      leer, und weil die Effekt-Abhaengigkeiten sich danach nie wieder
//      aenderten, wurde nie erneut gefragt. Der Superadmin sass vor einem
//      leeren Auswahlfeld, ohne Grund und ohne Weg zurueck — bis er die
//      ganze Seite neu lud.
//   2. Es gab die Liste zweimal: `useUsers` holt dieselben Daten vom selben
//      Endpunkt unter demselben Cache-Schluessel. Zwei Wege fuer eine Sache,
//      und einer davon war der schlechtere (Prinzip 1).
//
// Jetzt haengt die Leiste an `useUsers`. Damit wiederholt sich ein
// fehlgeschlagener Abruf von selbst, der Stand wird geteilt statt doppelt
// geholt — und wenn es trotzdem nicht klappt, sagt die Leiste das und bietet
// einen Knopf an, statt ein stummes leeres Feld zu zeigen.
//
// Der frueher hier stehende Kommentar «waehrend Impersonation blendet
// /api/users 403» war ueberdies falsch: der Endpunkt hat seit B8b gar keine
// Rollen-Schranke mehr. Die Liste wird waehrend einer Vertretung trotzdem
// nicht geholt — nicht weil sie verboten waere, sondern weil dann nur der
// Beenden-Knopf sichtbar ist und niemand sie braucht.

export function ImpersonateBar() {
  const { user, actedAsUserId, startImpersonate, stopImpersonate } = useAuth();

  const impersonating = actedAsUserId !== null;
  const visible = impersonating || (user !== null && isSuperadminRole(user.role));

  const targets = useUsers({ enabled: visible && !impersonating });

  if (!visible || !user) return null;

  if (impersonating) {
    return (
      <div className="flex items-center justify-between gap-4 border-b border-border bg-warning px-4 py-2 text-sm text-primary-foreground">
        <span>
          Impersonation aktiv — du handelst als{" "}
          <span className="font-mono">{user.fullName}</span> (
          {USER_ROLE_LABELS[user.role]}).
        </span>
        <button
          type="button"
          onClick={() => {
            void stopImpersonate();
          }}
          className="rounded-md border border-primary-foreground/40 px-2 py-1 text-xs hover:bg-primary-foreground/10"
        >
          Impersonation beenden
        </button>
      </div>
    );
  }

  const options = (targets.data ?? []).filter((u) => u.id !== user.id);

  return (
    <div className="flex items-center gap-3 border-b border-border bg-muted px-4 py-2 text-sm">
      <span className="text-muted-foreground" id="impersonate-bar-label">
        Impersonieren als:
      </span>
      <select
        aria-labelledby="impersonate-bar-label"
        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        disabled={targets.isPending}
        defaultValue=""
        onChange={(e) => {
          const targetId = e.target.value;
          if (!targetId) return;
          void startImpersonate(targetId);
        }}
      >
        <option value="">— User wählen —</option>
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {u.fullName} · {USER_ROLE_LABELS[u.role]}
          </option>
        ))}
      </select>
      {/* Ein leeres Feld ohne Erklaerung ist der Zustand, ueber den Daniel
          gestolpert ist. Klappt der Abruf nicht, steht das jetzt da — samt
          Knopf, statt «lade die Seite neu». */}
      {targets.isError ? (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          Liste nicht geladen.
          <button
            type="button"
            onClick={() => {
              void targets.refetch();
            }}
            className="rounded-md border border-input px-2 py-0.5 hover:bg-background"
          >
            Nochmal
          </button>
        </span>
      ) : null}
    </div>
  );
}
